import { Stack, Typography } from '@mui/material';
import { LinkedinTypes, ProductGroups } from 'enums';
import { FunnelTypes, RecruiterLocationSearch, RegularConstraint } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  ContactSearchMethods,
  ContactToAdd,
  LinkedinSearch,
  LinkedinSearchResult,
  SearchObjective,
  SearchToAdd,
  SingleSearch,
} from '../../../campaign-builder.types';
import OwnContactSearch from './own-contact-search/own-contact-search';
import PaiContactSearch from './pai-contact-search/pai-contact-search';
import SearchMethod from './search-method';
import UploadContactList from './upload-contact-list';
import AddContactsFromCampaign from './add-contacts-from-campaign/add-contacts-from-campaign';

interface Props {
  productGroup: ProductGroups;
  targetFunnelId: string;
  targetFunnelType: FunnelTypes;
  contactSearchMethod: ContactSearchMethods | '';

  linkedinProduct: LinkedinTypes | '';
  paiSearchLanguage: string[];
  includeOwnContacts: boolean;
  regularLocationConstraints: RegularConstraint;
  recruiterLocationConstraints: RecruiterLocationSearch;
  linkedinSearch: LinkedinSearch;

  selectedContactSearchObjective: SearchObjective | null;
  searchToAdd: SearchToAdd | null;
  linkedinAccounts: LinkedinAccount[];
  linkedinAccount: LinkedinAccount | undefined;
  separateLinkedinAccount: string;
  addSearchesLoading: boolean;

  contactsToAdd: ContactToAdd[];
  addContactsLoading: boolean;

  handleContactSearchMethodChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleLinkedinProductChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handlePaiSearchLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleIncludeOwnContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleLocationFieldChange(event: React.ChangeEvent<HTMLInputElement>): void;

  searchKeyUp(): void;

  searchKeyDown(): void;

  handleMultiSelectLocationsChange(value: string): () => void;

  handleMultiSelectLocationsRemove(value: string): () => void;

  handleSelectRecruiterLocationConstraint(searchType: 'geo' | 'zip'): (value: string) => () => void;

  handleRemoveRecruiterLocationConstraint(searchType: 'geo' | 'zip', urn: string): () => void;

  handleRecruiterLocationConstraintQueryChange(searchType: 'geo' | 'zip'): (event: React.ChangeEvent<HTMLInputElement>) => void;

  searchRecruiterLocationKeyUp(searchType: 'geo' | 'zip'): () => void;

  searchRecruiterLocationKeyDown(): void;

  handleRecruiterLocationAreaChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleResetLinkedinSearch(): void;

  handleResetLocationSettings(): void;

  handleStartLinkedinSearch(): void;

  handleOpenRefineLinkedinSearchDialog(linkedinSearch: LinkedinSearchResult, idx: number): () => void;

  handleOpenCreateNewSearchDialog(): void;

  onOpenFeedbackDialog?(): void;

  handleSelectContactSearchObjective(newObjective: SearchObjective): () => void;

  handleOwnSearchFieldChange(fieldType: keyof SingleSearch): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handlePeopleTypeChange?: (type: 'includeReactions' | 'includeComments' | 'includeAuthor') => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleProfileDegreeChange?: (value: 1 | 2 | 3) => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSeparateLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAddOwnLinkedinSearch(): void;

  handleAddOwnLinkedinSearchWithSkipQualification(): void;

  handleJobTitlesChange?: (titles: string[]) => void;

  onFileDrop(files: any): void;

  handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleDeleteContacts(): void;

  handleAddContactsFromFile(): void;
}

const AddContacts: React.FC<Props> = ({
  productGroup,
  targetFunnelId,
  targetFunnelType,
  contactSearchMethod,
  linkedinProduct,
  paiSearchLanguage,
  includeOwnContacts,
  regularLocationConstraints,
  recruiterLocationConstraints,
  linkedinSearch,
  selectedContactSearchObjective,
  searchToAdd,
  linkedinAccounts,
  linkedinAccount,
  separateLinkedinAccount,
  addSearchesLoading,
  contactsToAdd,
  addContactsLoading,
  handleContactSearchMethodChange,
  handleLinkedinProductChange,
  handlePaiSearchLanguageChange,
  handleIncludeOwnContactsChange,
  handleLocationFieldChange,
  searchKeyUp,
  searchKeyDown,
  handleMultiSelectLocationsChange,
  handleMultiSelectLocationsRemove,
  handleSelectRecruiterLocationConstraint,
  handleRemoveRecruiterLocationConstraint,
  handleRecruiterLocationConstraintQueryChange,
  searchRecruiterLocationKeyUp,
  searchRecruiterLocationKeyDown,
  handleRecruiterLocationAreaChange,
  handleResetLinkedinSearch,
  handleResetLocationSettings,
  handleStartLinkedinSearch,
  handleOpenRefineLinkedinSearchDialog,
  handleOpenCreateNewSearchDialog,
  onOpenFeedbackDialog,
  handleSelectContactSearchObjective,
  handleOwnSearchFieldChange,
  handlePeopleTypeChange,
  handleProfileDegreeChange,
  handleSeparateLinkedinAccountChange,
  handleAddOwnLinkedinSearch,
  handleAddOwnLinkedinSearchWithSkipQualification,
  handleJobTitlesChange,
  onFileDrop,
  handleFileChange,
  handleDeleteContacts,
  handleAddContactsFromFile,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Helper function to handle product-specific translations
  const getLocalizedText = (key: string): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey) || t(key);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey) || t(key);
  };

  /**
   * Content handlers
   */

  const getSearchMethodContent = () => {
    switch (contactSearchMethod) {
      case ContactSearchMethods.WE_SEARCH:
        return (
          <PaiContactSearch
            productGroup={productGroup}
            linkedinProduct={linkedinProduct}
            paiSearchLanguage={paiSearchLanguage}
            includeOwnContacts={includeOwnContacts}
            regularLocationConstraints={regularLocationConstraints}
            recruiterLocationConstraints={recruiterLocationConstraints}
            linkedinAccounts={linkedinAccounts}
            linkedinAccount={linkedinAccount}
            separateLinkedinAccount={separateLinkedinAccount}
            linkedinSearch={linkedinSearch}
            handleLinkedinProductChange={handleLinkedinProductChange}
            handlePaiSearchLanguageChange={handlePaiSearchLanguageChange}
            handleIncludeOwnContactsChange={handleIncludeOwnContactsChange}
            handleLocationFieldChange={handleLocationFieldChange}
            searchKeyUp={searchKeyUp}
            searchKeyDown={searchKeyDown}
            handleMultiSelectLocationsChange={handleMultiSelectLocationsChange}
            handleMultiSelectLocationsRemove={handleMultiSelectLocationsRemove}
            handleSelectRecruiterLocationConstraint={handleSelectRecruiterLocationConstraint}
            handleRemoveRecruiterLocationConstraint={handleRemoveRecruiterLocationConstraint}
            handleRecruiterLocationConstraintQueryChange={handleRecruiterLocationConstraintQueryChange}
            searchRecruiterLocationKeyUp={searchRecruiterLocationKeyUp}
            searchRecruiterLocationKeyDown={searchRecruiterLocationKeyDown}
            handleRecruiterLocationAreaChange={handleRecruiterLocationAreaChange}
            handleResetLinkedinSearch={handleResetLinkedinSearch}
            handleResetLocationSettings={handleResetLocationSettings}
            handleSeparateLinkedinAccountChange={handleSeparateLinkedinAccountChange}
            handleStartLinkedinSearch={handleStartLinkedinSearch}
            handleOpenRefineLinkedinSearchDialog={handleOpenRefineLinkedinSearchDialog}
            handleOpenCreateNewSearchDialog={handleOpenCreateNewSearchDialog}
            onOpenFeedbackDialog={onOpenFeedbackDialog}
          />
        );
      case ContactSearchMethods.OWN_SEARCH:
        return (
          <OwnContactSearch
            productGroup={productGroup}
            contactSearchMethod={contactSearchMethod}
            selectedContactSearchObjective={selectedContactSearchObjective}
            searchToAdd={searchToAdd}
            linkedinAccounts={linkedinAccounts}
            separateLinkedinAccount={separateLinkedinAccount}
            addSearchesLoading={addSearchesLoading}
            handleSelectContactSearchObjective={handleSelectContactSearchObjective}
            handleOwnSearchFieldChange={handleOwnSearchFieldChange}
            handlePeopleTypeChange={handlePeopleTypeChange}
            handleProfileDegreeChange={handleProfileDegreeChange}
            handleSeparateLinkedinAccountChange={handleSeparateLinkedinAccountChange}
            handleAddOwnLinkedinSearch={handleAddOwnLinkedinSearch}
            handleAddOwnLinkedinSearchWithSkipQualification={handleAddOwnLinkedinSearchWithSkipQualification}
            handleJobTitlesChange={handleJobTitlesChange}
          />
        );
      case ContactSearchMethods.UPLOAD_CONTACT_LIST:
        return (
          <UploadContactList
            contactSearchMethod={contactSearchMethod}
            contactsToAdd={contactsToAdd}
            addContactsLoading={addContactsLoading}
            onFileDrop={onFileDrop}
            handleFileChange={handleFileChange}
            handleDeleteContacts={handleDeleteContacts}
            handleAddContactsFromFile={handleAddContactsFromFile}
          />
        );
      case ContactSearchMethods.ADD_FROM_CAMPAIGN:
        return <AddContactsFromCampaign targetFunnelId={targetFunnelId} targetFunnelType={targetFunnelType} />;
      default:
        return <></>;
    }
  };

  const searchMethod = (
    <SearchMethod
      contactSearchMethod={contactSearchMethod}
      handleContactSearchMethodChange={handleContactSearchMethodChange}
      productGroup={productGroup}
    />
  );

  // KI search is the default: it stands alone, the expert sources fold away below it.
  if (contactSearchMethod === ContactSearchMethods.WE_SEARCH || contactSearchMethod === '') {
    return (
      <Stack className={classes.addContacts}>
        {getSearchMethodContent()}
        {searchMethod}
      </Stack>
    );
  }

  return (
    <Stack className={classes.addContacts} gap={2}>
      <section className={classes.section}>
        <div className={classes.titleContainer}>
          <Typography className={classes.blueTitle}>{getLocalizedText('campaignContactListTitle')}</Typography>
          <Typography className={classes.title}>{getLocalizedText('thisIsHowItGoes')}</Typography>
          <Typography className={classes.greyText}>{getLocalizedText('campaignContactListExplanation')}</Typography>
        </div>
      </section>
      {searchMethod}
      {getSearchMethodContent()}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  addContacts: {
    padding: '0 24px',
  },
  section: {
    position: 'relative',
    padding: '24px 0 24px 48px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  titleContainer: {
    flexBasis: '50%',
  },
  blueTitle: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
}));

export default AddContacts;
